import React from 'react';

import { Collapsible } from '../../components';

import { steps } from '../constants';
import { LookbookTopButtons, ToggleButton } from '../atoms';
import { LookbookFilterDropdown } from '../components/lookbook';
import { SortableProductList } from '../molecules';
import { useUpsertLookbook } from '../useUpsertLookbook';

const sortOptions = [
  { value: 'brand_asc', label: 'Brand Name: A → Z' },
  { value: 'brand_desc', label: 'Brand Name: Z → A' },
  { value: 'diameter_asc', label: 'Case Diameter: Low → High' },
  { value: 'diameter_desc', label: 'Case Diameter: High → Low' },
  { value: 'price_asc', label: 'Listing Price: Low → High' },
  { value: 'price_desc', label: 'Listing Price: High → Low' },
  { value: 'material', label: 'Case Material' },
];

export function ProductSelection() {
  const {
    getCollapsibleState,
    toggleCollapsible,
    goToHighlights,
    toggleBrandsVisibility,
    toggleCommentsVisibility,
    togglePricesVisibility,
    toggleUrlsVisibility,
    brandsHidden,
    commentsHidden,
    pricesHidden,
    urlsHidden,
    sort,
    updateSort,
    listingsOrder: items,
    setListingsOrder: setItems,
    listings,
    settings,
    changeNote,
    toggleListingHideUrl,
  } = useUpsertLookbook();
  const { isOpen, isVisible } = getCollapsibleState(steps.PRODUCTS);

  if (!isVisible) {
    return null;
  }

  // An id in `listingsOrder` can outlive its listing object (deleted upstream —
  // the hydrate request 404s). Those are skipped when rendering, so count the
  // ones that can actually draw rather than trusting the order's length.
  const renderableCount = (items ?? []).filter(
    (id) => listings?.[id] && listings[id].status !== "deleted"
  ).length;

  return (
    <Collapsible
      trigger="Product Selection"
      handleTriggerClick={() => toggleCollapsible(steps.PRODUCTS)}
      open={isOpen}
      testId="lookbook-products"
      className="lookbook-products"
      noContainer
      overflowWhenOpen="visible"
    >
      <div className="lb-product-selection">
        <div className="lb-product-selection__toolbar">
          <div className="lb-product-selection__sort">
            <LookbookFilterDropdown
              onChange={(val) => updateSort(val.value)}
              options={sortOptions}
              placeholder="Sort by"
              value={sort}
            />
          </div>

          <div className="lb-product-selection__toggle">
            <ToggleButton
              fullWidth
              label="Hide Brand Names"
              checked={brandsHidden}
              onChange={toggleBrandsVisibility}
            />
          </div>

          <div className="lb-product-selection__toggle">
            <ToggleButton
              fullWidth
              label="Hide Comments"
              checked={commentsHidden}
              onChange={toggleCommentsVisibility}
            />
          </div>

          <div className="lb-product-selection__toggle">
            <ToggleButton
              fullWidth
              label="Hide Prices"
              checked={pricesHidden}
              onChange={togglePricesVisibility}
            />
          </div>

          <div className="lb-product-selection__toggle">
            <ToggleButton
              fullWidth
              label="Hide URLs"
              checked={urlsHidden}
              onChange={toggleUrlsVisibility}
            />
          </div>

          <div className="lb-product-selection__next">
            <LookbookTopButtons onNext={goToHighlights} />
          </div>
        </div>

        {isOpen && renderableCount > 0 && (
          <SortableProductList
            items={items}
            setItems={setItems}
            listings={listings}
            settings={settings}
            changeNote={changeNote}
            toggleListingHideUrl={toggleListingHideUrl}
          />
        )}

        {isOpen && renderableCount === 0 && (
          <div className="lb-empty">
            <p className="lb-empty__title">No products to arrange yet</p>
            <p className="lb-empty__body">
              Choose listings in <strong>Select Listings</strong> above. They
              appear here so you can order them and set per-product options.
            </p>
          </div>
        )}
      </div>
    </Collapsible>
  );
}
